import { useMutation, useQuery } from "convex/react";
import { Capacitor } from "@capacitor/core";
import { ChevronRight, EyeOff, Info, ShieldPlus, Sparkles } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { AppIcon } from "../../components/apps/AppIcon";
import {
  TAB_BODY_COMPACT_TIMEFRAME_CLASS,
  TabPageHeader,
  TabPageTimeframe,
} from "../../components/navigation/TabPageHeader";
import { TabPageShell } from "../../components/navigation/TabPageShell";
import { BottomSheet } from "../habits/BottomSheet";
import { InsightExcludeSheet } from "./InsightExcludeSheet";
import { InsightChatSheet } from "./InsightChatSheet";
import { assignBrandAppColors } from "../../lib/apps/appUsageColors";
import { AppFriendlyName } from "../../lib/apps/AppFriendlyName";
import { useFriendlyAppName } from "../../lib/apps/useFriendlyAppName";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { addDays } from "../../lib/dateNav";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { mergeMarketingDemoRules, mergeMarketingDemoTargets } from "../../lib/marketing/demoRules";
import { useMarketingDemoMode, useMarketingDemoOverlay } from "../../lib/marketing/MarketingDemoProvider";
import { formatActivationCountForDisplay, formatDuration, todayKey } from "../../lib/format";
import {
  scoreInsightWindow,
  type InsightMetric,
  type InsightScores,
  type InsightWindowResult,
  type SmartBlockSuggestion,
} from "../../lib/insights/insightScoring";
import { buildActiveSmartBlockRows, type ActiveSmartBlockRow } from "../../lib/insights/activeSmartBlocks";
import { hasInsightChatUsageData } from "../../lib/insights/insightChatContext";
import { shouldUseIosSmartBlockActivationFlow } from "../../lib/insights/smartBlockSelection";
import { detectLocalAI, type LocalAIState } from "../../lib/native/ai";
import { triggerLightTabHaptic } from "../../lib/native/haptics";
import { canPickAndroidApps, canPickScreenTimeApps, persistAndroidSelection } from "../../lib/native/rules";
import type { Rule, Target, TargetSelection } from "../../lib/rules/ruleTypes";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import { SmartBlockIosActivationFlow } from "./SmartBlockIosActivationFlow";

export type Insight = {
  id: string;
  createdAt: number;
  title: string;
  teaser: string;
  body: string;
  actions: string[];
  engine: "local" | "cloud";
};

const INSIGHT_WINDOW_DAYS = 14;
const DEFAULT_COLOR = "#7ab89a";
const RANK_COUNT = 3;

/** Shell inline padding (36) + one column gap (10 @ gap-x-2.5). */
const INSIGHT_GRID_CHROME_PX = 46;

/** Rank #3 is the baseline circle; #1 and #2 scale up proportionally. */
const RANK_SCALE = {
  1: { maxWidth: 116, iconSize: 56, popoverOffset: 65 },
  2: { maxWidth: 99, iconSize: 47, popoverOffset: 58 },
  3: { maxWidth: 83, iconSize: 40, popoverOffset: 47 },
} as const;

/** Cap circle width so two ranked columns never exceed the mobile content area. */
function circleCellStyle(rank: 1 | 2 | 3): { width: string; maxWidth: string } {
  const scale = RANK_SCALE[rank];
  const rank1Max = RANK_SCALE[1].maxWidth;
  const cap = `calc((100vw - ${INSIGHT_GRID_CHROME_PX}px) / 2 * ${scale.maxWidth / rank1Max})`;
  const px = `${scale.maxWidth}px`;
  return { width: `min(${px}, ${cap})`, maxWidth: `min(${px}, ${cap})` };
}

type MetricKind = "compulsivity" | "doom";
type InfoSheetKind = MetricKind | null;

// Ring color encodes score severity inside the existing dark-green palette:
// calmer green when usage is light, warming toward amber/orange as the proxy
// score climbs. Thresholds: 0–39 calm, 40–69 elevated, 70–100 urgent.
const RING_TRACK = "oklch(26% 0.02 165)";
const RING_COLORS = {
  calm: "oklch(74% 0.10 155)", // low
  warm: "oklch(80% 0.10 95)", // moderate
  urgent: "oklch(72% 0.13 45)", // high
} as const;

function ringColorForScore(score: number): string {
  if (score >= 70) return RING_COLORS.urgent;
  if (score >= 40) return RING_COLORS.warm;
  return RING_COLORS.calm;
}

type ActivateSmartBlockResult = {
  ruleId: string;
  targetId: string;
  created: boolean;
  reactivated: boolean;
  reusedTarget: boolean;
};
const activateSmartBlockRef = mutationRef<
  {
    anonymousDeviceId: string;
    app: { displayName: string; appIdentifier?: string; category?: string };
    startHour: number;
    endHour: number;
    goal?: "doom" | "compulsivity";
    selection?: TargetSelection;
    asSmartBlock?: boolean;
  },
  ActivateSmartBlockResult
>("rules:activateSmartBlock");

type InsightPrefs = { excludedAppKeys: string[] };

const insightPrefsRef = queryRef<{ anonymousDeviceId: string }, InsightPrefs | null>("habitPrefs:getPrefs");
const upsertInsightPrefsRef = mutationRef<
  { anonymousDeviceId: string; patch: { excludedAppKeys?: string[] } },
  null
>("habitPrefs:upsertPrefs");

export function InsightTab() {
  const anonymousDeviceId = useDeviceId();
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const navigate = useNavigate();
  const endDate = todayKey();
  const startDate = addDays(endDate, -(INSIGHT_WINDOW_DAYS - 1));
  const rawReal = useQuery(
    queryRef<{ anonymousDeviceId: string; startDate: string; endDate: string }, InsightWindowResult>(
      "usageInsights:getRecentWindow",
    ),
    { anonymousDeviceId, startDate, endDate },
  );
  const raw = useMarketingDemoOverlay(
    "usageInsights:getRecentWindow",
    { anonymousDeviceId, startDate, endDate },
    rawReal,
  );
  const rulesReal = useQuery(queryRef<{ anonymousDeviceId: string }, Rule[]>("rules:list"), { anonymousDeviceId });
  const targetsReal = useQuery(queryRef<{ anonymousDeviceId: string }, Target[]>("targets:list"), { anonymousDeviceId });
  const { isActive: marketingDemoActive, seedApps: marketingSeedApps } = useMarketingDemoMode();
  const rules = useMemo(
    () => mergeMarketingDemoRules(rulesReal, marketingDemoActive, marketingSeedApps),
    [marketingDemoActive, marketingSeedApps, rulesReal],
  );
  const targets = useMemo(
    () => mergeMarketingDemoTargets(targetsReal, marketingDemoActive, marketingSeedApps),
    [marketingDemoActive, marketingSeedApps, targetsReal],
  );
  const prefs = useQuery(insightPrefsRef, { anonymousDeviceId });
  const upsertPrefs = useMutation(upsertInsightPrefsRef);
  const brandColorMap = useQuery(
    queryRef<Record<string, never>, Record<string, string>>("appBrandColors:getMap"),
    {},
  );

  const [excludedAppKeys, setExcludedAppKeys] = useState<string[]>([]);
  const prefsHydrated = useRef(false);

  useLayoutEffect(() => {
    if (prefs === undefined || prefsHydrated.current) return;
    prefsHydrated.current = true;
    if (prefs?.excludedAppKeys?.length) setExcludedAppKeys(prefs.excludedAppKeys);
  }, [prefs]);

  const persistExcluded = useCallback(
    (next: string[]) => {
      setExcludedAppKeys(next);
      void upsertPrefs({ anonymousDeviceId, patch: { excludedAppKeys: next } });
    },
    [anonymousDeviceId, upsertPrefs],
  );

  const scores = useMemo(() => {
    if (!raw || rules === undefined || targets === undefined) return null;
    return scoreInsightWindow(raw, {
      rules,
      targets,
      smartBlockCopy: t.smartBlock,
      languageCode,
      excludedAppKeys,
    });
  }, [raw, rules, targets, t.smartBlock, languageCode, excludedAppKeys]);
  const colorByAppKey = useMemo(() => buildColorMap(scores, brandColorMap), [brandColorMap, scores]);
  const activeSmartBlocks = useMemo(() => {
    if (!rules || !targets || !scores) return [];
    return buildActiveSmartBlockRows(
      rules,
      targets,
      scores.compulsivityRanked,
      scores.doomScrollRiskRanked,
      t.smartBlock,
      languageCode,
    );
  }, [rules, targets, scores, t.smartBlock, languageCode]);

  const activateSmartBlock = useMutation(activateSmartBlockRef);
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [aiState, setAIState] = useState<LocalAIState | null>(null);
  const [pendingBlock, setPendingBlock] = useState<SmartBlockSuggestion | null>(null);
  const [activating, setActivating] = useState(false);
  const useIosSmartBlockFlow = useMemo(
    () => shouldUseIosSmartBlockActivationFlow(canPickScreenTimeApps(), pendingBlock, rules, targets),
    [pendingBlock, rules, targets],
  );

  const navigateToActiveRule = useCallback(
    (ruleId: string) => {
      triggerLightTabHaptic();
      navigate("/rules", { state: { highlightRuleId: ruleId, highlightSource: "insight" } });
    },
    [navigate],
  );

  const finishActivation = async (
    resolvedAppName: string,
    result: ActivateSmartBlockResult,
  ) => {
    const bannerMessage = result.reactivated
      ? formatTemplate(t.insight.smartBlockActiveAgain, { appName: resolvedAppName })
      : result.created
        ? formatTemplate(t.insight.smartBlockOn, { appName: resolvedAppName })
        : formatTemplate(t.insight.smartBlockAlreadyBlocked, { appName: resolvedAppName });
    setPendingBlock(null);
    navigate("/rules", { state: { highlightRuleId: result.ruleId, bannerMessage } });
  };

  const confirmActivation = async (resolvedAppName: string) => {
    if (!pendingBlock) return;
    setActivating(true);
    try {
      // Android knows the package from usage data, so Smart Block activates directly
      // (no honor-system picker): persist the package as a native selection so the
      // enforcer can resolve and block it, then attach that selection to the rule.
      let selection: TargetSelection | undefined;
      if (canPickAndroidApps() && pendingBlock.appIdentifier) {
        const selectionId = crypto.randomUUID();
        const summary = await persistAndroidSelection(selectionId, [pendingBlock.appIdentifier]);
        if (summary.applicationCount > 0) {
          selection = {
            id: selectionId,
            applicationCount: summary.applicationCount,
            categoryCount: 0,
            webDomainCount: 0,
            updatedAt: Date.now(),
          };
        }
      }

      const result = await activateSmartBlock({
        anonymousDeviceId,
        app: {
          displayName: pendingBlock.displayName,
          appIdentifier: pendingBlock.appIdentifier,
          category: pendingBlock.category,
        },
        startHour: pendingBlock.startHour,
        endHour: pendingBlock.endHour,
        goal: pendingBlock.goal,
        ...(selection ? { selection, asSmartBlock: true } : {}),
      });
      await finishActivation(resolvedAppName, result);
    } finally {
      setActivating(false);
    }
  };

  const confirmIosActivation = async ({
    selection,
    asSmartBlock,
    resolvedAppName,
  }: {
    selection: TargetSelection;
    asSmartBlock: boolean;
    resolvedAppName: string;
  }) => {
    if (!pendingBlock) return;
    setActivating(true);
    try {
      const result = await activateSmartBlock({
        anonymousDeviceId,
        app: {
          displayName: pendingBlock.displayName,
          appIdentifier: pendingBlock.appIdentifier,
          category: pendingBlock.category,
        },
        startHour: pendingBlock.startHour,
        endHour: pendingBlock.endHour,
        goal: pendingBlock.goal,
        selection,
        asSmartBlock,
      });
      await finishActivation(resolvedAppName, result);
    } finally {
      setActivating(false);
    }
  };

  // Only one popover open at a time, keyed per circle.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [infoSheet, setInfoSheet] = useState<InfoSheetKind>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const detected = await detectLocalAI();
      if (!cancelled) setAIState(detected);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const otherInsightOverlayOpen =
    excludeOpen || infoSheet !== null || pendingBlock !== null || openKey !== null;
  const showInsightChatButton =
    aiState?.hasLocalAI === true &&
    raw !== undefined &&
    scores !== null &&
    hasInsightChatUsageData(scores) &&
    (chatOpen || !otherInsightOverlayOpen);

  useEffect(() => {
    if (!openKey) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openKey]);

  const compulsivity = scores?.compulsivityTop ?? [];
  const doom = scores?.doomScrollRiskTop ?? [];
  const hasCircles = compulsivity.length > 0 || doom.length > 0;
  const excludedCount = excludedAppKeys.length;

  return (
    <TabPageShell
      className={TAB_BODY_COMPACT_TIMEFRAME_CLASS}
      fixed={
        <TabPageHeader
          title={t.insight.title}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              {showInsightChatButton ? (
                <button
                  type="button"
                  onClick={() => {
                    triggerLightTabHaptic();
                    setChatOpen(true);
                  }}
                  className={`icon-button shrink-0${chatOpen ? " text-[oklch(72%_0.09_155)]" : ""}`}
                  aria-label={t.insightChat.fabAria}
                >
                  <Sparkles size={20} />
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setExcludeOpen(true)}
                className="icon-button relative shrink-0"
                aria-label={t.insight.openExclude}
              >
                <EyeOff size={20} />
                {excludedCount > 0 ? (
                  <span className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-[oklch(72%_0.09_155)] px-1 text-[10px] font-bold text-[oklch(12%_0.015_160)]">
                    {excludedCount}
                  </span>
                ) : null}
              </button>
            </div>
          }
        />
      }
    >
      <TabPageTimeframe>{formatTemplate(t.insight.timeframe, { days: INSIGHT_WINDOW_DAYS })}</TabPageTimeframe>

      {raw === undefined || scores === null ? <InsightSkeleton /> : null}

      {scores ? (
        <>
          <StatusNote scores={scores} t={t} />

          {hasCircles ? (
            <section className="grid gap-3">
              <div className="grid grid-cols-2 gap-x-2.5">
                <ColumnLabel onInfo={() => setInfoSheet("compulsivity")} metricLabel={t.insight.compulsivity}>{t.insight.compulsivity}</ColumnLabel>
                <ColumnLabel onInfo={() => setInfoSheet("doom")} metricLabel={t.insight.doomScrollRisk}>{t.insight.doomScrollRisk}</ColumnLabel>
              </div>
              <div className="grid gap-y-4">
                {Array.from({ length: RANK_COUNT }, (_, rankIndex) => {
                  const rank = (rankIndex + 1) as 1 | 2 | 3;
                  const placement = rankIndex === 0 ? "bottom" : rankIndex === RANK_COUNT - 1 ? "top" : "bottom";
                  return (
                    <div key={rank} className="grid grid-cols-2 items-center gap-x-2.5">
                      <MetricCircleCell
                        cellKey={`comp-${rankIndex}`}
                        metric={compulsivity[rankIndex]}
                        kind="compulsivity"
                        rank={rank}
                        color={resolveColor(compulsivity[rankIndex], colorByAppKey)}
                        placement={placement}
                        openKey={openKey}
                        setOpenKey={setOpenKey}
                      />
                      <MetricCircleCell
                        cellKey={`doom-${rankIndex}`}
                        metric={doom[rankIndex]}
                        kind="doom"
                        rank={rank}
                        color={resolveColor(doom[rankIndex], colorByAppKey)}
                        placement={placement}
                        openKey={openKey}
                        setOpenKey={setOpenKey}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}

          <MetricInfoSheet kind={infoSheet} onClose={() => setInfoSheet(null)} t={t} />

          <ActiveSmartBlocks
            rows={activeSmartBlocks}
            colorByAppKey={colorByAppKey}
            onNavigateToRule={navigateToActiveRule}
            t={t}
          />

          <SmartBlocks
            suggestions={scores.suggestions}
            colorByAppKey={colorByAppKey}
            onActivate={setPendingBlock}
            t={t}
          />
        </>
      ) : null}

      {useIosSmartBlockFlow ? (
        <SmartBlockIosActivationFlow
          suggestion={pendingBlock}
          busy={activating}
          onActivate={confirmIosActivation}
          onClose={() => setPendingBlock(null)}
        />
      ) : (
        <SmartBlockConfirmSheet
          suggestion={pendingBlock}
          busy={activating}
          onConfirm={(appName) => void confirmActivation(appName)}
          onClose={() => setPendingBlock(null)}
          t={t}
        />
      )}

      <InsightExcludeSheet
        open={excludeOpen}
        onClose={() => setExcludeOpen(false)}
        anonymousDeviceId={anonymousDeviceId}
        windowStartDate={startDate}
        windowEndDate={endDate}
        windowData={raw}
        excludedAppKeys={excludedAppKeys}
        onChange={persistExcluded}
      />

      <InsightChatSheet
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        anonymousDeviceId={anonymousDeviceId}
        startDate={startDate}
        endDate={endDate}
        excludedAppKeys={excludedAppKeys}
        aiState={aiState}
      />

      {/* Outside-tap dismissal: a transparent layer beneath the open popover. */}
      {openKey ? (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          className="fixed inset-0 z-40 cursor-default"
          onClick={() => setOpenKey(null)}
        />
      ) : null}
    </TabPageShell>
  );
}

function resolveColor(metric: InsightMetric | undefined, map: Map<string, string>): string {
  if (!metric) return DEFAULT_COLOR;
  return map.get(metric.appKey) ?? metric.color ?? DEFAULT_COLOR;
}

/** Assign brand colors across every distinct app shown so icons stay consistent. */
function buildColorMap(scores: InsightScores | null, brandColorMap?: Record<string, string>) {
  const map = new Map<string, string>();
  if (!scores) return map;
  const unique: InsightMetric[] = [];
  const seen = new Set<string>();
  for (const metric of [...scores.compulsivityTop, ...scores.doomScrollRiskTop]) {
    if (seen.has(metric.appKey)) continue;
    seen.add(metric.appKey);
    unique.push(metric);
  }
  const colored = assignBrandAppColors(
    unique.map((metric) => ({ ...metric })),
    brandColorMap,
    unique.length,
  );
  for (const metric of colored) {
    if (metric.color) map.set(metric.appKey, metric.color);
  }
  return map;
}

function ColumnLabel({ children, onInfo, metricLabel }: { children: ReactNode; onInfo: () => void; metricLabel: string }) {
  const t = getTranslations(useScreen0Language().languageCode);
  return (
    <div className="relative mx-auto w-fit max-w-full pr-3.5">
      <p className="whitespace-nowrap text-center text-[13px] font-semibold uppercase leading-tight tracking-wide text-[oklch(60%_0.035_155)]">
        {children}
      </p>
      <button
        type="button"
        onClick={onInfo}
        aria-label={formatTemplate(t.insight.aboutMetricAria, { metric: metricLabel })}
        className="liquid-glass-icon-ring absolute right-0 top-0 inline-flex size-[13px] items-center justify-center text-[8px] font-bold leading-none text-[oklch(58%_0.03_155)] transition-colors duration-150 hover:text-[oklch(72%_0.03_155)] active:scale-95"
      >
        <Info size={8} strokeWidth={2.5} aria-hidden />
      </button>
    </div>
  );
}

function MetricInfoSheet({ kind, onClose, t }: { kind: InfoSheetKind; onClose: () => void; t: ReturnType<typeof getTranslations> }) {
  const copy = kind === "compulsivity"
    ? {
        id: "compulsivity-info-title",
        title: t.insight.compulsivity,
        body: [t.insight.compulsivityBody1, t.insight.compulsivityBody2, t.insight.compulsivityBody3],
      }
    : kind === "doom"
      ? {
          id: "doom-info-title",
          title: t.insight.doomScrollRisk,
          body: [t.insight.doomBody1, t.insight.doomBody2, t.insight.doomBody3],
        }
      : null;

  return (
    <BottomSheet open={copy !== null} onClose={onClose} labelledBy={copy?.id}>
      {copy ? (
        <div className="grid gap-3 px-4 pt-4 pb-2">
          <h2 id={copy.id} className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
            {copy.title}
          </h2>
          <div className="grid gap-2.5">
            {copy.body.map((paragraph) => (
              <p key={paragraph} className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      ) : null}
    </BottomSheet>
  );
}

function InsightSkeleton() {
  const skeletonRanks = [1, 2, 3] as const;
  return (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-x-2.5">
        <span className="mx-auto h-3.5 w-24 rounded bg-[oklch(27%_0.02_165)]" />
        <span className="mx-auto h-3.5 w-28 rounded bg-[oklch(27%_0.02_165)]" />
      </div>
      <div className="grid gap-y-4">
        {skeletonRanks.map((rank) => (
          <div key={rank} className="grid grid-cols-2 items-center gap-x-2.5">
            <span
              className="aspect-square justify-self-center rounded-full bg-[oklch(21%_0.018_165)]"
              style={circleCellStyle(rank)}
            />
            <span
              className="aspect-square justify-self-center rounded-full bg-[oklch(21%_0.018_165)]"
              style={circleCellStyle(rank)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusNote({ scores, t }: { scores: InsightScores; t: ReturnType<typeof getTranslations> }) {
  if (scores.status === "missing_permission") {
    return (
      <section className="liquid-glass-panel grid gap-2 p-4">
        <h2 className="text-lg font-semibold">{t.insight.statusPermissionNeededTitle}</h2>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {Capacitor.getPlatform() === "android"
            ? t.insight.statusPermissionNeededBodyAndroid
            : t.insight.statusPermissionNeededBody}
        </p>
        <Link to="/today" className="primary-action mt-1 w-full text-center">
          {t.common.backToToday}
        </Link>
      </section>
    );
  }
  if (scores.status === "no_data") {
    return (
      <section className="liquid-glass-panel grid gap-2 p-4">
        <h2 className="text-lg font-semibold">{t.insight.statusNoUsageTitle}</h2>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {t.insight.statusNoUsageBody}
        </p>
        <Link to="/today" className="primary-action mt-1 w-full text-center">
          {t.common.backToToday}
        </Link>
      </section>
    );
  }
  if (scores.status === "insufficient_history") {
    return (
      <section className="section-band grid gap-1">
        <h2 className="text-base font-semibold">{t.insight.statusEarlyReadTitle}</h2>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {formatTemplate(t.insight.statusEarlyReadBody, {
            days: scores.daysWithData,
            dayWord: scores.daysWithData === 1 ? t.insight.daySingular : t.insight.dayPlural,
            windowLabel: scores.windowLabel,
          })}
        </p>
      </section>
    );
  }
  if (!scores.hasOpenSignal) {
    return (
      <section className="section-band grid gap-1">
        <h2 className="text-base font-semibold">{t.insight.statusLimitedOpensTitle}</h2>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {t.insight.statusLimitedOpensBody}
        </p>
      </section>
    );
  }
  return null;
}

function MetricCircleCell({
  cellKey,
  metric,
  kind,
  rank,
  color,
  placement,
  openKey,
  setOpenKey,
}: {
  cellKey: string;
  metric: InsightMetric | undefined;
  kind: MetricKind;
  rank: 1 | 2 | 3;
  color: string;
  placement: "top" | "bottom";
  openKey: string | null;
  setOpenKey: (key: string | null) => void;
}) {
  const scale = RANK_SCALE[rank];
  if (!metric) return <EmptyCircleCell rank={rank} />;

  const isOpen = openKey === cellKey;
  return (
    <div className="relative justify-self-center" style={circleCellStyle(rank)}>
      <MetricCircle
        metric={metric}
        kind={kind}
        rank={rank}
        color={color}
        isOpen={isOpen}
        onToggle={() => setOpenKey(isOpen ? null : cellKey)}
      />
      <MetricPopover metric={metric} placement={placement} isOpen={isOpen} popoverOffset={scale.popoverOffset} />
    </div>
  );
}

function MetricCircle({
  metric,
  kind,
  rank,
  color,
  isOpen,
  onToggle,
}: {
  metric: InsightMetric;
  kind: MetricKind;
  rank: 1 | 2 | 3;
  color: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const prefersReducedMotion = useReducedMotion();
  const t = getTranslations(useScreen0Language().languageCode);
  const appName = useFriendlyAppName(metric);
  const ringColor = ringColorForScore(metric.score);
  const scale = RANK_SCALE[rank];

  const radius = 52;
  const center = 60;
  const strokeWidth = 8;
  const circumference = 2 * Math.PI * radius;
  const targetOffset = circumference * (1 - Math.max(0, Math.min(100, metric.score)) / 100);

  const kindLabel = kind === "compulsivity" ? t.insight.kindCompulsivityLabel : t.insight.kindDoomLabel;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-label={formatTemplate(t.insight.metricCircleAria, { appName, kindLabel, score: metric.score })}
      className="relative aspect-square w-full rounded-full outline-none transition-transform duration-200 ease-out active:scale-[0.97]"
      style={circleCellStyle(rank)}
    >
      <svg viewBox="0 0 120 120" className="block h-full w-full -rotate-90">
        <circle cx={center} cy={center} r={radius} fill="none" stroke={RING_TRACK} strokeWidth={strokeWidth} />
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={prefersReducedMotion ? false : { strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: targetOffset }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        />
      </svg>

      <span className="pointer-events-none absolute inset-0 grid place-content-center">
        <AppIcon
          displayName={metric.displayName}
          appIdentifier={metric.appIdentifier}
          category={metric.category}
          color={color}
          size={scale.iconSize}
          iconSize={scale.iconSize}
          className="mx-auto"
        />
      </span>
    </button>
  );
}

function MetricPopover({
  metric,
  placement,
  isOpen,
  popoverOffset,
}: {
  metric: InsightMetric;
  placement: "top" | "bottom";
  isOpen: boolean;
  popoverOffset: number;
}) {
  const prefersReducedMotion = useReducedMotion();
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const appName = useFriendlyAppName(metric);
  const enterY = placement === "bottom" ? -6 : 6;
  const anchorStyle = placement === "bottom"
    ? { top: `calc(50% + ${popoverOffset}px)` }
    : { bottom: `calc(50% + ${popoverOffset}px)` };

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          role="dialog"
          aria-label={formatTemplate(t.insight.metricDetailsAria, { appName })}
          onClick={(event) => event.stopPropagation()}
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: enterY }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: enterY }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          style={anchorStyle}
          className="liquid-glass-popover absolute left-0 z-50 w-[9.25rem] max-w-[72vw] grid gap-1.5 p-2.5"
        >
          <p className="text-sm font-semibold leading-snug text-[oklch(88%_0.02_155)] break-words">{appName}</p>
          <p className="mono text-xl font-semibold leading-none">{metric.score}%</p>
          <p className="mono text-xs font-semibold text-[oklch(82%_0.02_155)]">{formatDuration(metric.totalSeconds, languageCode)}</p>
          <p className="text-[11px] leading-4 text-[oklch(62%_0.022_155)]">
            {formatTemplate(t.insight.metricActivationsCount, {
              count: formatActivationCountForDisplay(metric.opens, "integer"),
            })}
          </p>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function EmptyCircleCell({ rank }: { rank: 1 | 2 | 3 }) {
  return (
    <div className="justify-self-center" style={circleCellStyle(rank)}>
      <div
        className="aspect-square w-full rounded-full liquid-glass-dashed-cta"
        aria-hidden
      />
    </div>
  );
}

function ActiveSmartBlocks({
  rows,
  colorByAppKey,
  onNavigateToRule,
  t,
}: {
  rows: ActiveSmartBlockRow[];
  colorByAppKey: Map<string, string>;
  onNavigateToRule: (ruleId: string) => void;
  t: ReturnType<typeof getTranslations>;
}) {
  if (rows.length === 0) return null;

  return (
    <section
      className="section-band section-band-compact grid gap-2"
      aria-label={t.insight.activeSmartBlocksAria}
    >
      <h2 className="text-base font-semibold">{t.insight.activeSmartBlocksTitle}</h2>
      <div className="rules-row-list">
        {rows.map((row) => (
          <ActiveSmartBlockRowView
            key={row.ruleId}
            row={row}
            color={colorByAppKey.get(row.appKey) ?? DEFAULT_COLOR}
            onNavigate={() => onNavigateToRule(row.ruleId)}
            t={t}
          />
        ))}
      </div>
    </section>
  );
}

function ActiveSmartBlockRowView({
  row,
  color,
  onNavigate,
  t,
}: {
  row: ActiveSmartBlockRow;
  color: string;
  onNavigate: () => void;
  t: ReturnType<typeof getTranslations>;
}) {
  const appName = useFriendlyAppName(row);
  return (
    <button
      type="button"
      onClick={onNavigate}
      className="tap-row grid w-full grid-cols-[auto_1fr_auto] items-center gap-2.5 px-3 py-3 text-left transition-colors active:bg-[oklch(20%_0.02_165)]"
      aria-label={formatTemplate(t.insight.goToActiveSmartBlockRuleAria, { appName })}
    >
      <AppIcon
        displayName={row.displayName}
        appIdentifier={row.appIdentifier}
        category={row.category}
        color={color}
        size={28}
        iconSize={28}
        className="shrink-0"
      />
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <p className="truncate text-sm font-semibold">{appName}</p>
          {row.goalLabel ? <GoalBadge label={row.goalLabel} /> : null}
          <span className="liquid-glass-badge px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[oklch(68%_0.04_155)]">
            {t.insight.smartBlockBadge}
          </span>
        </div>
        <p className="truncate text-xs text-[oklch(68%_0.025_155)]">{row.windowLabel}</p>
      </div>
      <ChevronRight size={18} className="shrink-0 text-[oklch(58%_0.03_155)]" aria-hidden />
    </button>
  );
}

function SmartBlocks({
  suggestions,
  colorByAppKey,
  onActivate,
  t,
}: {
  suggestions: SmartBlockSuggestion[];
  colorByAppKey: Map<string, string>;
  onActivate: (suggestion: SmartBlockSuggestion) => void;
  t: ReturnType<typeof getTranslations>;
}) {
  return (
    <section className="section-band grid gap-3">
      <div className="grid gap-1">
        <h2 className="text-lg font-semibold">{t.insight.smartBlockSuggestionsTitle}</h2>
        <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
          {t.insight.smartBlockSuggestionsBody}
        </p>
      </div>

      {suggestions.length > 0 ? (
        <div className="rules-row-list">
          {suggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion.id}
              suggestion={suggestion}
              color={colorByAppKey.get(suggestion.appKey) ?? DEFAULT_COLOR}
              onActivate={() => onActivate(suggestion)}
              t={t}
            />
          ))}
        </div>
      ) : (
        <div className="rules-row-list px-4 py-4">
          <p className="font-semibold">{t.insight.smartBlockEmptyTitle}</p>
          <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
            {t.insight.smartBlockEmptyBody}
          </p>
          <Link to="/today" className="primary-action mt-3 w-full text-center">
            {t.common.backToToday}
          </Link>
        </div>
      )}
    </section>
  );
}

function GoalBadge({ label }: { label: string }) {
  return (
    <span className="liquid-glass-badge px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[oklch(78%_0.06_155)]">
      {label}
    </span>
  );
}

function SuggestionRow({
  suggestion,
  color,
  onActivate,
  t,
}: {
  suggestion: SmartBlockSuggestion;
  color: string;
  onActivate: () => void;
  t: ReturnType<typeof getTranslations>;
}) {
  const appName = useFriendlyAppName(suggestion);
  const isReactivate = suggestion.mode === "reactivate";
  return (
    <button
      type="button"
      onClick={onActivate}
      className="tap-row grid w-full grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-3 text-left transition-colors active:bg-[oklch(20%_0.02_165)]"
      aria-label={
        isReactivate
          ? formatTemplate(t.insight.reactivateSmartBlockAria, { appName })
          : formatTemplate(t.insight.activateSmartBlockAria, { appName })
      }
    >
      <AppIcon
        displayName={suggestion.displayName}
        appIdentifier={suggestion.appIdentifier}
        category={suggestion.category}
        color={color}
        size={32}
        iconSize={32}
        className="mt-0.5 shrink-0 self-start"
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-semibold">{appName}</p>
          <GoalBadge label={suggestion.goalLabel} />
        </div>
        <p className="text-sm text-[oklch(78%_0.02_155)]">{suggestion.actionLabel}</p>
        {suggestion.reactivateHint ? (
          <p className="mt-1 text-xs font-semibold leading-5 text-[oklch(72%_0.09_155)]">
            {suggestion.reactivateHint}
          </p>
        ) : null}
        <p className="mt-1 text-xs leading-5 text-[oklch(62%_0.022_155)]">{suggestion.reason}</p>
      </div>
      <span className="shrink-0 self-center text-[oklch(72%_0.09_155)]" aria-hidden>
        <ShieldPlus size={18} />
      </span>
    </button>
  );
}

function SmartBlockConfirmSheet({
  suggestion,
  busy,
  onConfirm,
  onClose,
  t,
}: {
  suggestion: SmartBlockSuggestion | null;
  busy: boolean;
  onConfirm: (appName: string) => void;
  onClose: () => void;
  t: ReturnType<typeof getTranslations>;
}) {
  const appName = useFriendlyAppName({
    displayName: suggestion?.displayName,
    appIdentifier: suggestion?.appIdentifier,
    category: suggestion?.category,
  });
  const isReactivate = suggestion?.mode === "reactivate";
  const blockPrefix = formatTemplate(t.smartBlock.actionLabel, { range: "" }).trimEnd();
  const windowCopy = suggestion?.actionLabel.replace(`${blockPrefix} `, "") ?? "";
  return (
    <BottomSheet open={suggestion !== null} onClose={onClose} labelledBy="smart-block-confirm-title">
      {suggestion ? (
        <div className="grid gap-4 px-4 pt-4 pb-2">
          <div className="grid gap-1.5">
            <h2 id="smart-block-confirm-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
              {isReactivate ? t.insight.reactivateSmartBlockTitle : t.insight.activateSmartBlockTitle}
            </h2>
            <p className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
              {isReactivate
                ? formatTemplate(t.insight.reactivateConfirmBody, { appName, window: windowCopy })
                : formatTemplate(t.insight.activateConfirmBody, { appName, window: windowCopy })}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="secondary-action flex-1">{t.common.cancel}</button>
            <button type="button" onClick={() => onConfirm(appName)} className="primary-action flex-1" disabled={busy}>
              <ShieldPlus size={16} aria-hidden />
              {isReactivate ? t.insight.reactivate : t.insight.activate}
            </button>
          </div>
        </div>
      ) : null}
    </BottomSheet>
  );
}
